import { DownloadIcon, UploadIcon } from '@radix-ui/react-icons';
import { Grid } from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { ErrorBoundary } from 'components/common/error-boundary';
import { FlexTableWrapper } from 'components/common/layout/flex-table-wrapper';
import { SectionHeader } from 'components/sections/header';
import { VideoLibrarySidebar } from 'components/sections/video-library/sidebar';
import { VideoLibraryTable } from 'components/sections/video-library/table';
import { VideoUploadDialog } from 'components/sections/video-library/upload-dialog';
import { lightFormat, parseISO } from 'date-fns';
import { t } from 'i18next';
import { IVerboseComponent } from 'interfaces/i-components';
import { MiscHelper } from 'lib_ts/classes/misc.helper';
import { LOCAL_DATE_FORMAT } from 'lib_ts/enums/datetime.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { useEffect, useMemo, useState } from 'react';
import { VideosService } from 'services/videos.service';
import { STATIC_PREFIX, useVideosStore } from 'stores/use-videos-store';

export const VideoLibrary = (
  props: IVerboseComponent & {
    // for admin portal where filters are not visible/used
    static?: boolean;
  }
) => {
  const filter = useVideosStore((s) => s.filter);
  const allVideos = useVideosStore((s) => s.videos);
  const selectedVideo = useVideosStore((s) => s.selectedVideo);

  const safeVideos = useMemo(() => {
    if (props.static) {
      return allVideos.filter((m) => m.video_path.startsWith(STATIC_PREFIX));
    }

    const output = allVideos
      .filter((m) => !m.video_path.startsWith(STATIC_PREFIX))
      .filter(
        (m) =>
          filter.teamName.length === 0 ||
          filter.teamName.includes(m.TeamName as string)
      )
      .filter(
        (m) =>
          filter.pitcher.length === 0 ||
          filter.pitcher.includes(m.PitcherFullName as string)
      )
      .filter(
        (m) => filter.type.length === 0 || filter.type.includes(m.PitchType)
      )
      .filter(
        (m) =>
          filter.delivery.length === 0 ||
          filter.delivery.includes(m.DeliveryType as string)
      )
      .filter(
        (m) =>
          filter.dateAdded.length === 0 ||
          filter.dateAdded.includes(
            lightFormat(parseISO(m._created), LOCAL_DATE_FORMAT)
          )
      );

    return output;
  }, [props.static, filter, allVideos]);

  // reset to no video selected when unmounting
  useEffect(() => {
    return () => {
      useVideosStore.setState({ selectedVideo: undefined });
    };
  }, []);

  const [dialogUpload, setDialogUpload] = useState<number>();

  return (
    <ErrorBoundary identifier={props.identifier}>
      <Grid
        style={{
          height: '100%', // for vFlex to work
        }}
        columns={selectedVideo ? '2' : '1'}
        gap="4"
      >
        <FlexTableWrapper
          gap={RADIX.FLEX.GAP.SECTION}
          header={
            <SectionHeader
              header={props.static ? '' : t('main.video-library')}
              actions={[
                {
                  prefixIcon: <DownloadIcon />,
                  label: 'common.export-all',
                  variant: RADIX.BUTTON.VARIANT.SECONDARY,
                  invisible: safeVideos.length === 0,
                  onClick: async () => {
                    const ids = safeVideos.map((v) => v._id);

                    const csvString =
                      await VideosService.getInstance().exportCsv(ids);

                    if (!csvString) {
                      NotifyHelper.error({
                        message_md: t('common.request-failed-msg'),
                      });
                      return;
                    }

                    const blob = new Blob([csvString], {
                      type: 'text/csv',
                    });

                    MiscHelper.saveAs(
                      blob,
                      `video-library_${lightFormat(
                        new Date(),
                        'yyyy-MM-dd'
                      )}.csv`
                    );
                  },
                },
                {
                  prefixIcon: <UploadIcon />,
                  label: 'videos.upload-videos',
                  onClick: () => setDialogUpload(Date.now()),
                },
              ]}
            />
          }
          table={
            <VideoLibraryTable
              static={!!props.static}
              videos={safeVideos}
              afterChangeSelected={(v) =>
                useVideosStore.setState({ selectedVideo: v })
              }
            />
          }
        />

        {selectedVideo && <VideoLibrarySidebar />}
      </Grid>

      {dialogUpload && (
        <VideoUploadDialog
          key={dialogUpload}
          static={!!props.static}
          onClose={() => setDialogUpload(undefined)}
        />
      )}
    </ErrorBoundary>
  );
};
